import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import {
  ColDef,
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  StateUpdatedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, take, takeUntil } from 'rxjs';
import 'ag-grid-enterprise';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UserGridStateService } from '../user-grid-state.service';
import { AuthService } from '../auth.service';
import { canViewUfosUser } from '../work-orders/ufos/ufos.types';
import {
  WorkOrderStatusFlowDialogComponent,
  WorkOrderStatusFlowDialogResult,
} from './work-order-status-flow-dialog.component';
import {
  WorkOrderStatusMilestone,
  WorkOrderV2ProductDeliveryResponse,
  WorkOrderV2Row,
  WorkOrderV2StatusPayload,
  WorkOrderV2StatusResponse,
  WorkOrdersScope,
  WorkOrdersV2Column,
  WorkOrdersV2Request,
  WorkOrdersV2Stats,
} from './work-orders-v2.models';
import { WorkOrdersV2Service } from './work-orders-v2.service';
import { WorkOrderPanelComponent } from './work-order-panel/work-order-panel.component';
import { WorkOrderPanelTarget } from './work-order-panel/work-order-panel.models';
import { isPanelActionTarget, panelTargetForColumn } from './work-order-panel/work-order-panel.navigation';

type InsightKey = 'total' | 'is_open' | 'is_today' | 'is_late' | 'is_at_risk' | 'needs_appointment' | 'has_drop_trailer';

interface WorkOrdersScopeOption {
  key: WorkOrdersScope;
  label: string;
  description: string;
}

interface WorkOrdersInsight {
  key: InsightKey;
  label: string;
  value: number;
  icon: string;
  tone: 'slate' | 'sky' | 'emerald' | 'amber' | 'orange' | 'rose' | 'violet';
  title?: string;
}

interface WorkOrderStatusEventTime {
  date: string;
  time: string;
  timezone: string;
  label: string;
}

interface WorkOrderStatusFlowProgress {
  completed: number;
  label: string;
  nextStatus: string | null;
}

interface UfosAvailabilityBlock {
  uuids: string[];
  requested: boolean;
  request: Subscription;
}

const EMPTY_STATS: WorkOrdersV2Stats = {
  total: 0,
  open: 0,
  today: 0,
  late: 0,
  at_risk: 0,
  needs_appointment: 0,
  hot: 0,
  expedited: 0,
  drop_trailer: 0,
  exceptions: 0,
  billing_blockers: 0,
  missing_docs: 0,
};

@Component({
  selector: 'app-work-orders-v2',
  templateUrl: './work-orders-v2.component.html',
  styleUrls: ['./work-orders-v2.component.scss']
})
export class WorkOrdersV2Component implements OnInit, OnDestroy {
  readonly scopeOptions: WorkOrdersScopeOption[] = [
    { key: 'current', label: 'Current', description: 'Current non-cancelled work orders' },
    { key: 'active', label: 'Active', description: 'Open and undelivered work orders' },
    { key: 'operational', label: 'Operational', description: 'Three months, plus open work from six months' },
    { key: 'cancelled', label: 'Cancelled', description: 'Cancelled and voided work orders' },
    { key: 'all', label: 'All', description: 'All retained work orders, including cancelled' },
  ];

  readonly rowModelType = 'serverSide';
  readonly themeClass = 'ag-theme-ulaap';
  readonly getRowId = (params: GetRowIdParams<WorkOrderV2Row>): string => params.data.lading_uuid;

  themeMode: 'dark' | 'light' = 'dark';
  scope: WorkOrdersScope = 'current';
  quickFilterInput = '';
  quickFilter = '';
  activeInsightKey: InsightKey | '' = '';
  gridReady = false;
  loading = true;
  statsLoading = true;
  hasLoaded = false;
  errorMessage = '';
  stats: WorkOrdersV2Stats = { ...EMPTY_STATS };
  initialState: GridState | undefined;
  columnDefs: ColDef<WorkOrderV2Row>[] = [];

  readonly defaultColDef: ColDef<WorkOrderV2Row> = {
    sortable: true,
    filter: 'agTextColumnFilter',
    resizable: true,
    minWidth: 110,
    filterParams: {
      buttons: ['apply', 'clear'],
      closeOnApply: true,
      debounceMs: 250,
    },
  };

  readonly gridOptions: GridOptions<WorkOrderV2Row> & { serverSideStoreType?: 'partial' | 'full' } = {
    rowModelType: 'serverSide',
    serverSideStoreType: 'partial',
    cacheBlockSize: 50,
    maxBlocksInCache: 6,
    maxConcurrentDatasourceRequests: 2,
    blockLoadDebounceMillis: 80,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 250],
    rowHeight: 58,
    headerHeight: 46,
    animateRows: false,
    suppressCellFocus: false,
    suppressRowClickSelection: true,
    getRowId: this.getRowId,
    sideBar: {
      toolPanels: ['columns', 'filters'],
      position: 'right',
    },
    onCellDoubleClicked: (event: CellDoubleClickedEvent<WorkOrderV2Row>) => this.openCellPanel(event),
    onCellKeyDown: (event: CellKeyDownEvent<WorkOrderV2Row>) => this.onCellKeyDown(event),
    onFilterChanged: () => this.resetUfosAvailability(),
    onSortChanged: () => this.resetUfosAvailability(),
  };

  private gridApi: GridApi<WorkOrderV2Row> | null = null;
  private baselineStats: WorkOrdersV2Stats = { ...EMPTY_STATS };
  private viewGeneration = 0;
  private dataRequestSequence = 0;
  private latestDataRequest = 0;
  private statsRequestSequence = 0;
  private latestStatsRequest = 0;
  private columnsConfigured = false;
  private readonly destroy$ = new Subject<void>();
  private canViewUfos = false;
  private readonly ufosAvailability = new Map<string, boolean>();
  private readonly ufosBlocks = new Map<string, UfosAvailabilityBlock>();
  private readonly searchChanges$ = new Subject<string>();
  private readonly stateChanges$ = new Subject<GridState>();
  private readonly themeStorageKey = 'work-orders-v2-theme';
  private readonly scopeStorageKey = 'work-orders-v2-scope';
  private readonly insightFields: InsightKey[] = [
    'is_open',
    'is_today',
    'is_late',
    'is_at_risk',
    'needs_appointment',
    'has_drop_trailer',
  ];

  private readonly statusMilestones: WorkOrderStatusMilestone[] = [
    { status: 'Booked', label: 'Booked', shortLabel: 'Book' },
    { status: 'En Route Shipper', label: 'En route to pickup', shortLabel: 'To PU' },
    { status: 'Arrived Shipper', label: 'At pickup', shortLabel: 'At PU' },
    { status: 'Loaded Shipper', label: 'Picked up', shortLabel: 'Picked' },
    { status: 'En Route Consignee', label: 'En route to delivery', shortLabel: 'To DEL' },
    { status: 'Arrived Consignee', label: 'At delivery', shortLabel: 'At DEL' },
    { status: 'Delivered Consignee', label: 'Delivered', shortLabel: 'Done' },
  ];

  /** Optional and terminal statuses resolve to the closest universal milestone. */
  private readonly statusProgressByCurrent: Record<string, number> = {
    'Qualify': 0,
    'Unassigned': 0,
    'Booked': 1,
    'Order FulFillment': 1,
    'En Route Shipper': 2,
    'Arrived Shipper': 3,
    'Loading Shipper': 3,
    'Detained Shipper': 3,
    'Loaded Shipper': 4,
    'Shipped': 4,
    'Load Completed': 4,
    'In Transit': 4,
    'En Route Terminal': 4,
    'Arrived Terminal': 4,
    'At Terminal': 4,
    'Departed Terminal': 4,
    'En Route Consignee': 5,
    'Out for Delivery': 5,
    'Arrived Consignee': 6,
    'Unloading Consignee': 6,
    'Detained Consignee': 6,
    'Delivered Consignee': 7,
    'Carrier Invoiced': 7,
    'Installation complete': 7,
  };

  private readonly presentationColumns: ColDef<WorkOrderV2Row>[] = [
    {
      colId: 'open_details',
      headerName: '',
      width: 58,
      minWidth: 58,
      maxWidth: 58,
      pinned: 'left',
      lockPinned: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.actionsRenderer(params),
    },
    {
      field: 'order_label',
      headerName: 'Work Order',
      width: 175,
      minWidth: 160,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.orderRenderer(params),
    },
    {
      field: 'status_label',
      headerName: 'Status Flow',
      width: 265,
      minWidth: 235,
      maxWidth: 330,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.statusFlowRenderer(params),
    },
    {
      field: 'route_label',
      headerName: 'Route',
      width: 250,
      minWidth: 215,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.routeRenderer(params),
    },
    {
      field: 'party_label',
      headerName: 'Customer / Carrier',
      width: 230,
      minWidth: 195,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.partyRenderer(params),
    },
    {
      field: 'schedule_label',
      headerName: 'Schedule',
      width: 205,
      minWidth: 185,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.scheduleRenderer(params),
    },
    {
      field: 'freight_summary_label',
      headerName: 'Freight / Equipment',
      width: 205,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.freightRenderer(params),
    },
    {
      field: 'latest_note_preview',
      headerName: 'Latest Note',
      minWidth: 240,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<WorkOrderV2Row>) => this.noteRenderer(params),
    },
  ];

  constructor(
    private readonly workOrders: WorkOrdersV2Service,
    private readonly gridState: UserGridStateService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly zone: NgZone,
    private readonly auth: AuthService,
  ) {
    this.columnDefs = this.presentationColumns.map(column => ({ ...column }));
  }

  ngOnInit(): void {
    this.auth.loadCurrentUser().pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: user => {
        this.canViewUfos = canViewUfosUser(user?.user_id);
        if (!this.canViewUfos) {
          this.resetUfosAvailability();
          return;
        }
        // User context can arrive after the first grid block.
        for (const [key, block] of this.ufosBlocks) this.requestUfosAvailability(key, block);
      },
      error: () => {
        this.canViewUfos = false;
        this.resetUfosAvailability();
      },
    });
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    this.themeMode = storedTheme === 'light' ? 'light' : 'dark';

    const storedScope = window.localStorage.getItem(this.scopeStorageKey);
    if (this.isScope(storedScope)) {
      this.scope = storedScope;
    }

    this.searchChanges$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      this.quickFilter = value.trim();
      if (this.activeInsightKey !== '') {
        this.activeInsightKey = '';
        this.stats = { ...this.baselineStats };
        this.clearInsightFilters();
      }
      this.refreshData();
    });

    this.stateChanges$.pipe(
      debounceTime(600),
      takeUntil(this.destroy$),
    ).subscribe(state => this.gridState.saveUserGridState('work_orders_v2', state));

    this.gridState.getUserGridState('work_orders_v2').pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(state => {
      this.initialState = state ?? undefined;
      this.gridReady = true;
    });
  }

  ngOnDestroy(): void {
    this.resetUfosAvailability();
    this.gridApi = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  get insights(): WorkOrdersInsight[] {
    return [
      { key: 'total', label: 'Matching', value: this.stats.total, icon: 'fa-layer-group', tone: 'slate' },
      { key: 'is_open', label: 'Open', value: this.stats.open, icon: 'fa-route', tone: 'sky' },
      { key: 'is_today', label: 'Today', value: this.stats.today, icon: 'fa-calendar-day', tone: 'emerald' },
      { key: 'is_late', label: 'Late', value: this.stats.late, icon: 'fa-clock', tone: 'rose' },
      { key: 'is_at_risk', label: 'At Risk', value: this.stats.at_risk, icon: 'fa-triangle-exclamation', tone: 'amber' },
      {
        key: 'needs_appointment',
        label: 'Needs Appt',
        value: this.stats.needs_appointment,
        icon: 'fa-calendar-xmark',
        tone: 'orange',
        title: 'Created in the last 14 days, not yet shipped, and no appointment is recorded',
      },
      { key: 'has_drop_trailer', label: 'Drop Trailer', value: this.stats.drop_trailer, icon: 'fa-trailer', tone: 'violet' },
    ];
  }

  get activeScope(): WorkOrdersScopeOption {
    return this.scopeOptions.find(option => option.key === this.scope) ?? this.scopeOptions[0];
  }

  onGridReady(event: GridReadyEvent<WorkOrderV2Row>): void {
    this.gridApi = event.api;
    event.api.setGridOption('serverSideDatasource', this.createDatasource());
  }

  onSearchChange(value: string): void {
    this.searchChanges$.next(value);
  }

  setScope(scope: WorkOrdersScope): void {
    if (scope === this.scope) {
      return;
    }

    this.scope = scope;
    window.localStorage.setItem(this.scopeStorageKey, scope);
    this.activeInsightKey = '';
    this.stats = { ...this.baselineStats };
    this.clearInsightFilters();
    this.refreshData();
  }

  applyInsight(key: InsightKey): void {
    if (!this.gridApi) {
      return;
    }

    const filterModel = { ...this.gridApi.getFilterModel() } as Record<string, unknown>;
    for (const field of this.insightFields) {
      delete filterModel[field];
    }

    this.viewGeneration += 1;
    if (key === 'total' || this.activeInsightKey === key) {
      this.activeInsightKey = '';
    } else {
      filterModel[key] = {
        filterType: 'set',
        values: ['true'],
      };
      this.activeInsightKey = key;
    }

    this.stats = { ...this.baselineStats };
    this.gridApi.setFilterModel(filterModel);
  }

  isInsightActive(key: InsightKey): boolean {
    return this.activeInsightKey === key;
  }

  refreshData(): void {
    this.resetUfosAvailability();
    this.viewGeneration += 1;
    this.errorMessage = '';
    this.gridApi?.paginationGoToFirstPage();
    this.gridApi?.refreshServerSide({ purge: true });
  }

  clearFilters(): void {
    this.resetUfosAvailability();
    this.viewGeneration += 1;
    this.quickFilterInput = '';
    this.quickFilter = '';
    this.activeInsightKey = '';
    this.stats = { ...this.baselineStats };
    this.gridApi?.setFilterModel(null);
    this.gridApi?.refreshServerSide({ purge: true });
  }

  resetView(): void {
    this.clearFilters();
    this.gridApi?.resetColumnState();
    this.gridApi?.closeToolPanel();
  }

  toggleColumnsPanel(): void {
    if (!this.gridApi) {
      return;
    }

    if (this.gridApi.getOpenedToolPanel() === 'columns') {
      this.gridApi.closeToolPanel();
    } else {
      this.gridApi.openToolPanel('columns');
    }
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  @ViewChild(WorkOrderPanelComponent) private workOrderPanel?: WorkOrderPanelComponent;

  openDetails(row: WorkOrderV2Row | undefined, target?: WorkOrderPanelTarget): void {
    if (!row?.lading_uuid) return;
    void this.workOrderPanel?.open(row, target);
  }

  private openCellPanel(event: CellDoubleClickedEvent<WorkOrderV2Row> | CellKeyDownEvent<WorkOrderV2Row>): void {
    if (this.dialog.openDialogs.length || isPanelActionTarget(event.event?.target)) return;
    const target = panelTargetForColumn(event.column.getColId());
    this.openDetails(event.data, target);
  }

  private onCellKeyDown(event: CellKeyDownEvent<WorkOrderV2Row>): void {
    const keyboard = event.event;
    if (!(keyboard instanceof KeyboardEvent) || keyboard.key !== 'Enter') return;
    if (keyboard.altKey || keyboard.ctrlKey || keyboard.metaKey || keyboard.shiftKey) return;
    if (isPanelActionTarget(keyboard.target)) return;
    keyboard.preventDefault();
    this.openCellPanel(event);
  }

  onPanelSaved(): void {
    // Keep the datasource, filters, pagination, and scroll position in place.
    // The server re-evaluates rows that moved out of the current filter.
    this.gridApi?.refreshServerSide({ purge: false });
  }

  canLeaveWorkOrders(): Promise<boolean> {
    return this.workOrderPanel?.canLeave() ?? Promise.resolve(true);
  }

  onStateUpdated(event: StateUpdatedEvent<WorkOrderV2Row>): void {
    const sources = event.sources ?? [];
    if (
      sources.includes('columnSizing') ||
      sources.includes('columnOrder') ||
      sources.includes('columnVisibility') ||
      sources.includes('columnPinning') ||
      sources.includes('sideBar') ||
      sources.includes('filter')
    ) {
      this.stateChanges$.next(event.state);
    }
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams<WorkOrderV2Row>) => {
        const startRow = Number(params.request.startRow ?? 0);
        const endRow = Number(params.request.endRow ?? startRow + 50);
        const ufosBlockKey = `${startRow}:${endRow}`;
        const ufosBlock = this.beginUfosBlock(ufosBlockKey);
        const filterModel = (params.request.filterModel ?? {}) as Record<string, unknown>;
        const sortModel = params.request.sortModel ?? [];
        const includeStats = startRow <= 0;
        const requestGeneration = this.viewGeneration;
        const dataRequest = ++this.dataRequestSequence;
        this.latestDataRequest = dataRequest;
        const statsRequest = includeStats ? ++this.statsRequestSequence : 0;

        if (includeStats) {
          this.latestStatsRequest = statsRequest;
          this.statsLoading = true;
        }

        const request: WorkOrdersV2Request = {
          startRow,
          endRow,
          serverSide: true,
          filterModel,
          sortModel,
          quickFilter: this.quickFilter,
          scope: this.scope,
          includeStats,
        };

        this.loading = true;

        this.workOrders.load(request).pipe(
          finalize(() => {
            this.loading = false;
          }),
          takeUntil(this.destroy$),
        ).subscribe({
          next: response => {
            if (!response.ok) {
              if (includeStats) {
                this.statsLoading = false;
              }
              this.errorMessage = response.message || 'The work-order API rejected the request.';
              params.fail();
              return;
            }

            if (!this.columnsConfigured && response.columns.length > 0) {
              this.configureColumns(response.columns);
            }

            if (
              dataRequest === this.latestDataRequest &&
              requestGeneration === this.viewGeneration &&
              this.activeInsightKey === ''
            ) {
              this.baselineStats = { ...this.baselineStats, total: response.totalRows };
              this.stats = { ...this.baselineStats };
            }
            if (
              includeStats &&
              statsRequest === this.latestStatsRequest &&
              requestGeneration === this.viewGeneration &&
              this.activeInsightKey === ''
            ) {
              if (response.stats !== null) {
                this.baselineStats = response.stats;
                this.stats = { ...response.stats };
              }
              this.statsLoading = false;
            }

            this.errorMessage = '';
            this.hasLoaded = true;
            if (this.ufosBlocks.get(ufosBlockKey) === ufosBlock) {
              ufosBlock.uuids = [...new Set(response.data.map(row => row.lading_uuid.trim().toLowerCase()).filter(Boolean))];
              for (const uuid of ufosBlock.uuids) this.ufosAvailability.delete(uuid);
            }
            params.success({
              rowData: response.data,
              rowCount: response.totalRows,
            });
            if (this.ufosBlocks.get(ufosBlockKey) === ufosBlock) {
              this.requestUfosAvailability(ufosBlockKey, ufosBlock);
            }
          },
          error: (error: unknown) => {
            if (includeStats && statsRequest === this.latestStatsRequest) {
              this.statsLoading = false;
            }
            this.errorMessage = this.readError(error);
            params.fail();
          },
        });
      },
    };
  }

  private configureColumns(metadata: WorkOrdersV2Column[]): void {
    const metaByField = new Map(metadata.map(column => [column.field, column]));
    const presentedFields = new Set(
      this.presentationColumns
        .map(column => column.field)
        .filter((field): field is string => typeof field === 'string'),
    );

    const presented = this.presentationColumns.map(column => {
      const field = typeof column.field === 'string' ? column.field : null;
      const meta = field ? metaByField.get(field) : undefined;
      return meta ? this.applyMetadata(column, meta) : { ...column };
    });

    const additional = metadata
      .filter(column => !presentedFields.has(column.field))
      .map(column => this.columnFromMetadata(column));

    this.columnsConfigured = true;
    this.columnDefs = [...presented, ...additional];
    this.gridApi?.setGridOption('columnDefs', this.columnDefs);
  }

  private applyMetadata(definition: ColDef<WorkOrderV2Row>, meta: WorkOrdersV2Column): ColDef<WorkOrderV2Row> {
    return {
      ...definition,
      headerName: definition.headerName || meta.header,
      headerTooltip: `${meta.header} · ${meta.group}`,
      sortable: meta.sortable,
      filter: this.filterFor(meta),
      filterParams: this.filterParamsFor(meta),
    };
  }

  private columnFromMetadata(meta: WorkOrdersV2Column): ColDef<WorkOrderV2Row> {
    const definition: ColDef<WorkOrderV2Row> = {
      field: meta.field,
      headerName: meta.header,
      headerTooltip: `${meta.header} · ${meta.group}`,
      hide: !meta.default_visible,
      sortable: meta.sortable,
      filter: this.filterFor(meta),
      filterParams: this.filterParamsFor(meta),
      minWidth: meta.type === 'boolean' ? 135 : 150,
    };

    if (meta.type === 'boolean') {
      definition.valueFormatter = (params: ValueFormatterParams<WorkOrderV2Row>) => this.booleanLabel(params.value);
    } else if (['date', 'timestamp'].includes(meta.type)) {
      definition.valueFormatter = (params: ValueFormatterParams<WorkOrderV2Row>) => this.dateLabel(params.value);
    }

    return definition;
  }

  private filterFor(meta: WorkOrdersV2Column): string | boolean {
    if (!meta.filterable) {
      return false;
    }
    if (meta.type === 'boolean') {
      return 'agSetColumnFilter';
    }
    if (meta.type === 'number') {
      return 'agNumberColumnFilter';
    }
    if (meta.type === 'date' || meta.type === 'timestamp') {
      return 'agDateColumnFilter';
    }
    return 'agTextColumnFilter';
  }

  private filterParamsFor(meta: WorkOrdersV2Column): Record<string, unknown> | undefined {
    if (!meta.filterable) {
      return undefined;
    }
    if (meta.type === 'boolean') {
      return {
        values: ['true', 'false'],
        valueFormatter: (params: { value: unknown }) => this.booleanLabel(params.value),
      };
    }
    return {
      buttons: ['apply', 'clear'],
      closeOnApply: true,
      debounceMs: 250,
    };
  }

  private actionsRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const root = document.createElement('div');
    root.className = 'wo2-row-actions';

    const openButton = document.createElement('button');
    openButton.type = 'button';
    openButton.className = 'wo2-open-button';
    openButton.title = 'Open work order';
    openButton.setAttribute('aria-label', 'Open work order');
    openButton.setAttribute('aria-controls', 'wo-order-panel');
    openButton.setAttribute('aria-haspopup', 'dialog');
    openButton.innerHTML = '<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>';
    openButton.addEventListener('click', event => {
      event.stopPropagation();
      this.zone.run(() => this.openDetails(params.data));
    });
    root.appendChild(openButton);
    return root;
  }

  private statusFlowRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const root = document.createElement('div');
    root.className = 'wo2-status-flow';

    const progress = this.statusFlowProgress(params.data);
    const meta = document.createElement('div');
    meta.className = 'wo2-status-flow-meta';

    const label = document.createElement('span');
    label.className = 'wo2-status-flow-label';
    label.textContent = progress.label;
    label.title = this.currentStatusFor(params.data) || progress.label;

    const metaActions = document.createElement('span');
    metaActions.className = 'wo2-status-flow-actions';

    const count = document.createElement('span');
    count.className = 'wo2-status-flow-count';
    count.textContent = `${progress.completed}/${this.statusMilestones.length}`;

    const manage = document.createElement('button');
    manage.type = 'button';
    manage.className = 'wo2-status-flow-manage';
    manage.title = 'Manage status milestone dates and times';
    manage.setAttribute('aria-label', manage.title);
    manage.innerHTML = '<i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>';
    manage.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.zone.run(() => this.openStatusFlowManager(params.data));
    });

    metaActions.append(count, manage);
    meta.append(label, metaActions);

    const bar = document.createElement('div');
    bar.className = 'wo2-status-flow-bar';
    this.statusMilestones.forEach((milestone, index) => {
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.className = 'wo2-status-flow-segment';
      const isComplete = index < progress.completed;
      const isNext = progress.nextStatus === milestone.status;
      segment.classList.add(isComplete ? 'is-complete' : isNext ? 'is-next' : 'is-upcoming');
      segment.title = `${index + 1}. ${milestone.label} · ${milestone.status}${isNext ? ' · Next milestone' : ''}`;
      segment.setAttribute('aria-label', segment.title);

      const icon = document.createElement('span');
      icon.className = 'wo2-status-flow-segment-icon';
      icon.innerHTML = isComplete
        ? '<i class="fa-solid fa-check" aria-hidden="true"></i>'
        : isNext
          ? String(index + 1)
          : '';
      segment.appendChild(icon);

      segment.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.zone.run(() => {
          if (isNext) {
            this.confirmNextStatus(params, milestone.status, segment);
          } else {
            this.openStatusFlowManager(params.data, milestone.status);
          }
        });
      });
      bar.appendChild(segment);
    });

    const nextLabel = progress.nextStatus
      ? this.statusMilestones.find(milestone => milestone.status === progress.nextStatus)?.label
      : '';
    root.title = `${progress.completed} of ${this.statusMilestones.length} milestones complete.${nextLabel ? ` Next: ${nextLabel}.` : ''}`;
    root.append(meta, bar);
    return root;
  }

  private openStatusFlowManager(row: WorkOrderV2Row | undefined, selectedStatus?: string): void {
    if (!row?.lading_uuid) return;
    this.dialog.open<
      WorkOrderStatusFlowDialogComponent,
      { row: WorkOrderV2Row; milestones: WorkOrderStatusMilestone[]; selectedStatus?: string; themeMode: 'dark' | 'light' },
      WorkOrderStatusFlowDialogResult
    >(WorkOrderStatusFlowDialogComponent, {
      data: {
        row,
        milestones: this.statusMilestones,
        selectedStatus,
        themeMode: this.themeMode,
      },
      autoFocus: false,
      maxWidth: '96vw',
      panelClass: 'work-order-status-flow-dialog',
    }).afterClosed().pipe(
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(result => {
      if (result?.changed) {
        this.gridApi?.refreshServerSide({ purge: false });
      }
    });
  }

  private confirmNextStatus(
    params: ICellRendererParams<WorkOrderV2Row>,
    nextStatus: string,
    button: HTMLButtonElement,
  ): void {
    const row = params.data;
    if (!row?.lading_uuid || button.disabled) {
      return;
    }

    const currentStatus = this.currentStatusFor(row) || 'Status not set';
    const orderLabel = this.text(row.order_label) || row.lading_uuid;
    const eventTime = this.browserStatusEventTime();

    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Set ${nextStatus}?`,
        message: `${orderLabel}\n${currentStatus} → ${nextStatus}\n\nEvent time: ${eventTime.label}\nConsignee local time will be calculated by the server.`,
        confirmText: `Set ${nextStatus}`,
        cancelText: 'Not yet',
        tone: nextStatus === 'Delivered Consignee' ? 'warning' : 'primary',
      }
    }).afterClosed().pipe(
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(confirmed => {
      if (confirmed === true) {
        this.prepareNextStatusSave(params, nextStatus, eventTime, button);
      }
    });
  }

  private prepareNextStatusSave(
    params: ICellRendererParams<WorkOrderV2Row>,
    nextStatus: string,
    eventTime: WorkOrderStatusEventTime,
    button: HTMLButtonElement,
  ): void {
    if (nextStatus !== 'Delivered Consignee' || !params.data?.lading_uuid) {
      this.saveNextStatus(params, nextStatus, eventTime, button, false);
      return;
    }

    const originalMarkup = button.innerHTML;
    this.setStatusButtonBusy(button, 'Checking');
    this.workOrders.getProductDeliveryPreview(params.data.lading_uuid).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: response => {
        this.restoreStatusButton(button, originalMarkup);
        if (!this.productDeliveryResponseSucceeded(response)) {
          this.toastr.error(response.message || 'Could not check product delivery timestamps. The status was not saved.');
          return;
        }

        const missingCount = this.responseCount(response.missing_delivery_count);
        if (missingCount === 0) {
          this.saveNextStatus(params, nextStatus, eventTime, button, false);
          return;
        }

        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Mark products delivered?',
            message: `This lading has ${missingCount} ${missingCount === 1 ? 'product' : 'products'} without a delivery time.\n\nApply this delivery milestone time to ${missingCount === 1 ? 'it' : 'them'}? Existing product delivery times will not be changed.`,
            confirmText: 'Mark delivered',
            cancelText: 'Status only',
            tone: 'warning',
          }
        }).afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(confirmed => {
          this.saveNextStatus(params, nextStatus, eventTime, button, confirmed === true);
        });
      },
      error: () => {
        this.restoreStatusButton(button, originalMarkup);
        this.toastr.error('Could not check product delivery timestamps. The status was not saved.');
      },
    });
  }

  private saveNextStatus(
    params: ICellRendererParams<WorkOrderV2Row>,
    nextStatus: string,
    eventTime: WorkOrderStatusEventTime,
    button: HTMLButtonElement,
    markProductsDelivered: boolean,
  ): void {
    const row = params.data;
    if (!row?.lading_uuid) {
      return;
    }

    const payload: WorkOrderV2StatusPayload = {
      lading_uuid: row.lading_uuid,
      lading_status: nextStatus,
      event_date: eventTime.date,
      event_time: eventTime.time,
      event_timezone: eventTime.timezone,
      override_local_time: false,
      lading_status_note: null,
    };

    const originalMarkup = button.innerHTML;
    this.setStatusButtonBusy(button, 'Saving');

    this.workOrders.addStatus(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        if (!this.statusMutationSucceeded(response)) {
          this.restoreStatusButton(button, originalMarkup);
          this.toastr.error(response.message || 'The next status could not be saved.');
          return;
        }

        params.node.setData({
          ...row,
          lading_status: nextStatus,
          lading_status_updated: response.data?.lading_status_updated ?? row.lading_status_updated,
          status_label: nextStatus,
          status_tone: nextStatus === 'Delivered Consignee' ? 'success' : 'info',
        });
        this.toastr.success(response.message || `${nextStatus} saved.`);

        if (!markProductsDelivered) {
          this.finishNextStatusSave(button, originalMarkup);
          return;
        }

        const deliveredTimeUtc = this.statusDeliveryTimeUtc(response.data);
        if (!deliveredTimeUtc) {
          this.toastr.error('The status was saved, but its delivery time could not be applied to products.');
          this.finishNextStatusSave(button, originalMarkup);
          return;
        }

        this.workOrders.markProductsDelivered(row.lading_uuid, deliveredTimeUtc).pipe(
          takeUntil(this.destroy$),
        ).subscribe({
          next: productResponse => {
            if (this.productDeliveryResponseSucceeded(productResponse)) {
              const updated = this.responseCount(productResponse.updated_count);
              this.toastr.success(`${updated} ${updated === 1 ? 'product was' : 'products were'} marked delivered.`);
            } else {
              this.toastr.error(productResponse.message || 'The status was saved, but the products were not updated.');
            }
            this.finishNextStatusSave(button, originalMarkup);
          },
          error: () => {
            this.toastr.error('The status was saved, but the products were not updated.');
            this.finishNextStatusSave(button, originalMarkup);
          },
        });
      },
      error: error => {
        this.restoreStatusButton(button, originalMarkup);
        this.toastr.error(this.readStatusError(error));
      }
    });
  }

  private statusFlowProgress(row: WorkOrderV2Row | undefined): WorkOrderStatusFlowProgress {
    const status = this.currentStatusFor(row);
    if (status === 'Cancelled') {
      return { completed: 0, label: 'Cancelled', nextStatus: null };
    }

    const knownPreFlow = status === '' || status === 'Qualify' || status === 'Unassigned';
    const completed = knownPreFlow ? 0 : this.statusProgressByCurrent[status];
    if (completed === undefined) {
      return { completed: 0, label: status || 'Status not set', nextStatus: null };
    }

    const milestone = this.statusMilestones.find(item => item.status === status);
    return {
      completed,
      label: milestone?.label || status || 'Not started',
      nextStatus: completed < this.statusMilestones.length
        ? this.statusMilestones[completed].status
        : null,
    };
  }

  private currentStatusFor(row: WorkOrderV2Row | undefined): string {
    return this.text(row?.lading_status) || this.text(row?.status_label);
  }

  private finishNextStatusSave(button: HTMLButtonElement, originalMarkup: string): void {
    this.restoreStatusButton(button, originalMarkup);
    this.gridApi?.refreshServerSide({ purge: false });
  }

  private setStatusButtonBusy(button: HTMLButtonElement, label: string): void {
    button.disabled = true;
    button.classList.add('is-saving');
    button.innerHTML = `<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i><span class="wo2-status-flow-segment-busy">${label}</span>`;
  }

  private restoreStatusButton(button: HTMLButtonElement, markup: string): void {
    if (!button.isConnected) return;
    button.disabled = false;
    button.classList.remove('is-saving');
    button.innerHTML = markup;
  }

  private browserStatusEventTime(): WorkOrderStatusEventTime {
    const now = new Date();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const pad = (value: number): string => String(value).padStart(2, '0');
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const formatted = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(now);

    return {
      date,
      time,
      timezone,
      label: `${formatted} (${timezone})`,
    };
  }

  private statusMutationSucceeded(response: WorkOrderV2StatusResponse | null | undefined): boolean {
    return response?.success === true || String(response?.code) === '200';
  }

  private productDeliveryResponseSucceeded(
    response: WorkOrderV2ProductDeliveryResponse | null | undefined,
  ): boolean {
    return response?.success === true || String(response?.code) === '200';
  }

  private responseCount(value: string | number | null | undefined): number {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  private statusDeliveryTimeUtc(savedStatus: WorkOrderV2StatusResponse['data']): string | null {
    if (!savedStatus?.local_time || !savedStatus.time_zone_id) return null;
    const deliveryTime = DateTime.fromSQL(savedStatus.local_time, { zone: savedStatus.time_zone_id });
    return deliveryTime.isValid ? deliveryTime.toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : null;
  }

  private readStatusError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
      if (error.status === 0) {
        return 'DAMS could not reach the status API.';
      }
      return `The next status could not be saved (${error.status}).`;
    }
    return 'The next status could not be saved.';
  }

  private orderRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const root = this.twoLineCell(
      this.text(params.data?.order_label) || 'Unnumbered order',
      this.text(params.data?.secondary_reference_label) || this.text(params.data?.lading_uuid),
      'wo2-order-cell',
    );
    const uuid = this.text(params.data?.lading_uuid).toLowerCase();
    if (this.canViewUfos && this.ufosAvailability.get(uuid) === true) {
      const primary = root.querySelector<HTMLElement>('.wo2-cell-primary');
      if (primary) {
        primary.classList.add('wo2-order-primary');
        const label = document.createElement('span');
        label.className = 'wo2-order-number';
        label.textContent = primary.textContent;
        primary.replaceChildren(label);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wo2-ufos-button';
        button.textContent = '🛸';
        button.title = 'UFOs data available';
        button.setAttribute('aria-label', 'UFOs data available');
        button.addEventListener('mousedown', event => event.stopPropagation());
        button.addEventListener('dblclick', event => event.stopPropagation());
        button.addEventListener('keydown', event => event.stopPropagation());
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          if (this.canViewUfos && this.ufosAvailability.get(uuid) === true) {
            this.zone.run(() => void this.router.navigate(['/work_order_details', uuid], { queryParams: { tab: 'ufos' } }));
          }
        });
        primary.appendChild(button);
      }
    }
    return root;
  }

  private beginUfosBlock(key: string): UfosAvailabilityBlock {
    const previous = this.ufosBlocks.get(key);
    if (previous) {
      previous.request.unsubscribe();
      for (const uuid of previous.uuids) this.ufosAvailability.delete(uuid);
      this.ufosBlocks.delete(key);
      this.refreshUfosCells();
    }
    const block: UfosAvailabilityBlock = { uuids: [], requested: false, request: new Subscription() };
    this.ufosBlocks.set(key, block);
    // The grid retains six blocks. Keep only a small additional lookup buffer.
    if (this.ufosBlocks.size > 12) {
      const oldestKey = this.ufosBlocks.keys().next().value as string;
      const oldest = this.ufosBlocks.get(oldestKey);
      oldest?.request.unsubscribe();
      for (const uuid of oldest?.uuids || []) this.ufosAvailability.delete(uuid);
      this.ufosBlocks.delete(oldestKey);
    }
    return block;
  }

  private requestUfosAvailability(key: string, block: UfosAvailabilityBlock): void {
    if (!this.canViewUfos || block.requested || !block.uuids.length || this.ufosBlocks.get(key) !== block) return;
    block.requested = true;
    // Normal grid blocks have 50 rows; keep the API's 100-UUID cap explicit.
    for (let offset = 0; offset < block.uuids.length; offset += 100) {
      const uuids = block.uuids.slice(offset, offset + 100);
      block.request.add(this.workOrders.loadUfosAvailability(uuids).pipe(takeUntil(this.destroy$)).subscribe({
        next: response => {
          if (!this.canViewUfos || this.ufosBlocks.get(key) !== block) return;
          const availability = response.ok ? response.data?.availability : undefined;
          for (const uuid of uuids) this.ufosAvailability.set(uuid, availability?.[uuid] === true);
          this.refreshUfosCells();
        },
        // An optional badge lookup never fails the main work-order grid.
        error: () => {
          if (this.ufosBlocks.get(key) !== block) return;
          for (const uuid of uuids) this.ufosAvailability.delete(uuid);
          this.refreshUfosCells();
        },
      }));
    }
  }

  private resetUfosAvailability(): void {
    for (const block of this.ufosBlocks.values()) block.request.unsubscribe();
    this.ufosBlocks.clear();
    this.ufosAvailability.clear();
    this.refreshUfosCells();
  }

  private refreshUfosCells(): void {
    // Avoid asking AG Grid to redraw synchronously inside its getRows lifecycle.
    queueMicrotask(() => this.gridApi?.refreshCells({ columns: ['order_label'], force: true }));
  }

  private routeRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const secondary = [this.text(params.data?.shipper_name), this.text(params.data?.consignee_name)]
      .filter(Boolean)
      .join(' → ');
    return this.twoLineCell(this.text(params.data?.route_label) || 'Route not set', secondary, 'wo2-route-cell');
  }

  private partyRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    return this.twoLineCell(
      this.text(params.data?.customer_name) || 'Customer not set',
      this.text(params.data?.carrier_name) || 'Carrier not assigned',
      'wo2-party-cell',
    );
  }

  private scheduleRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const appointment = this.text(params.data?.appointment_window_label);
    return this.twoLineCell(
      this.text(params.data?.schedule_label) || 'Schedule not set',
      appointment ? `Appointment ${appointment}` : '',
      'wo2-schedule-cell',
    );
  }

  private freightRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const freight = [
      this.valueWithLabel(params.data?.load_pieces, 'pcs'),
      this.valueWithLabel(params.data?.load_pallets, 'pal'),
      this.valueWithLabel(params.data?.load_weight, this.text(params.data?.load_weight_unit) || 'lb'),
    ].filter(Boolean).join(' · ');
    const equipment = [this.text(params.data?.type_of_equipment), this.text(params.data?.carrier_trailer)]
      .filter(Boolean)
      .join(' · ');
    return this.twoLineCell(freight || 'Freight not set', equipment, 'wo2-freight-cell');
  }

  private noteRenderer(params: ICellRendererParams<WorkOrderV2Row>): HTMLElement {
    const root = document.createElement('div');
    root.className = 'wo2-note-cell';
    root.textContent = this.text(params.data?.latest_note_preview) || 'No recent note';
    root.title = root.textContent;
    return root;
  }

  private twoLineCell(primaryText: string, secondaryText: string, extraClass: string): HTMLElement {
    const root = document.createElement('div');
    root.className = `wo2-two-line-cell ${extraClass}`;

    const primary = document.createElement('div');
    primary.className = 'wo2-cell-primary';
    primary.textContent = primaryText;
    primary.title = primaryText;
    root.appendChild(primary);

    if (secondaryText) {
      const secondary = document.createElement('div');
      secondary.className = 'wo2-cell-secondary';
      secondary.textContent = secondaryText;
      secondary.title = secondaryText;
      root.appendChild(secondary);
    }

    return root;
  }

  private clearInsightFilters(): void {
    if (!this.gridApi) {
      return;
    }
    const model = { ...this.gridApi.getFilterModel() } as Record<string, unknown>;
    for (const field of this.insightFields) {
      delete model[field];
    }
    this.gridApi.setFilterModel(model);
  }

  private isScope(value: string | null): value is WorkOrdersScope {
    return value !== null && this.scopeOptions.some(option => option.key === value);
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
      if (error.status === 0) {
        return 'DAMS could not reach the work-order API.';
      }
      return `Work orders could not be loaded (${error.status}).`;
    }
    return 'Work orders could not be loaded.';
  }

  private text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
  }

  private valueWithLabel(value: unknown, label: string): string {
    const text = this.text(value);
    return text === '' ? '' : `${text} ${label}`;
  }

  private booleanLabel(value: unknown): string {
    return value === true || value === 'true' || value === 1 || value === '1' ? 'Yes' : 'No';
  }

  private dateLabel(value: unknown): string {
    const text = this.text(value);
    if (text === '') {
      return '';
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return text;
    }
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
